// src/dashboard/Dashboard.tsx
import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Card,
    CardContent,
    Avatar,
    Chip,
    Container,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useNavigate } from 'react-router-dom';
import { IAccount } from '../Interfaces/IAccount';
import {
    useDeleteCalendarEntry,
    useGetCalendarEntries,
    useGetCalendarEntry,
    useToggleCalendarEntryFavorite,
    useUpdateCalendarEntry,
} from '../hooks/calendarEntry';
import { useGetUsers } from '../hooks/users';
import UserAvatar from '../accounts/UserAvatar';
import { ICalendarEntry } from '../Interfaces/ICalendarEntry';
import { ErrorTryRefetch } from '../core/ErrorTryRefetch';
import CalendarEventEdit from '../calendar/CalendarEventEdit';
import { useDashboardEventMedia } from '../hooks/dashboardEventMedia';
import EventMediaMosaic from './EventMediaMosaic';
import { useStorageTreeQuery } from '../hooks/storage';
import { IFolderTreeNode } from '../Interfaces/IStorage';
import { useAuthenticatedImage } from '../hooks/useAuthenticatedImage';
import { useBlogPosts } from '../blog/blogHooks';
import { useModuleSettings } from '../hooks/moduleSettings';
import { moduleEnabled } from '../utils/moduleEnabled';
import { getCurrentUserId } from '../utils/permission';
import { GREEN_700, GREEN_800, GREEN_900, cardSx, cardHoverSx, makeIconCircleSx, STATUS_TONES, StatusTone } from '../core/pvtaStyles';
import StatusChip from '../core/StatusChip';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Flache Hero-Button-Variante (bewusst ohne Schatten, sitzt auf grünem Verlauf).
const primaryButtonSx = {
    backgroundColor: GREEN_800,
    color: '#ffffff',
    borderRadius: 999,
    fontWeight: 700,
    boxShadow: 'none',
    '&:hover': { backgroundColor: GREEN_900, boxShadow: 'none' },
} as const;

// Mobile-sichere Card-Basis: nie breiter als der verfügbare Platz, schrumpfbar
// als Flex-Item (verhindert den min-width:auto-Overflow-Bug bei noWrap-Inhalten).
const safeCardSx = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
} as const;

type EventRsvpCounts = {
    going: number | null;
    notGoing: number | null;
    maybe: number | null;
};

const extractRsvpCounts = (entry: ICalendarEntry): EventRsvpCounts => ({
    going: entry.counts?.rsvp.going ?? null,
    notGoing: entry.counts?.rsvp.not_going ?? null,
    maybe: entry.counts?.rsvp.maybe ?? null,
});

// Definition der Teilnehmer-Status-Kacheln (Zusagen / Offen / Absagen).
const RSVP_STAT_DEFS: { key: keyof EventRsvpCounts; tone: StatusTone; label: string; icon: React.ReactNode }[] = [
    { key: 'going', tone: 'success', label: 'Zusagen', icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> },
    { key: 'maybe', tone: 'warning', label: 'Offen', icon: <HelpOutlineIcon sx={{ fontSize: 18 }} /> },
    { key: 'notGoing', tone: 'danger', label: 'Absagen', icon: <CancelOutlinedIcon sx={{ fontSize: 18 }} /> },
];

// Gruppierte, gleich breite Status-Kacheln statt loser Chips – klar lesbar auf Mobile.
const RsvpStats: React.FC<{ counts: EventRsvpCounts }> = ({ counts }) => {
    const visible = RSVP_STAT_DEFS.filter((def) => counts[def.key] !== null);
    if (visible.length === 0) {
        return null;
    }
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))`,
                gap: 1,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
            }}
        >
            {visible.map((def) => {
                const tone = STATUS_TONES[def.tone];
                return (
                    <Stack
                        key={def.key}
                        alignItems="center"
                        spacing={0.25}
                        sx={{
                            minWidth: 0,
                            py: 1.25,
                            px: 0.5,
                            borderRadius: 'var(--pvta-radius-sm)',
                            backgroundColor: tone.bg,
                            border: '1px solid',
                            borderColor: tone.border,
                            color: tone.color,
                        }}
                    >
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                            {def.icon}
                            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>
                                {counts[def.key]}
                            </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.02em', textAlign: 'center' }}>
                            {def.label}
                        </Typography>
                    </Stack>
                );
            })}
        </Box>
    );
};


const flattenFolderTree = (nodes: IFolderTreeNode[]): IFolderTreeNode[] => (
    nodes.flatMap((node) => [node, ...flattenFolderTree(node.children)])
);

const formatDate = (dateStr: string): string =>
    new Date(dateStr).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });

const formatTime = (dateStr: string): string =>
    `${new Date(dateStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`;

const formatLongDate = (dateStr: string): string =>
    new Date(dateStr).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });

// Markenbild als Fallback, wenn die Veranstaltung (noch) kein eigenes Foto hat.
const HERO_FALLBACK_IMAGE = `${process.env.PUBLIC_URL}/icons/Teutonia-Alemannia-5-1600s.webp`;

// Großflächiges, mit Auth geladenes Hintergrundbild der Hero-Karte.
const HeroBackground: React.FC<{ url: string | null }> = ({ url }) => {
    const { src } = useAuthenticatedImage(url);
    const resolvedSrc = src ?? HERO_FALLBACK_IMAGE;
    return (
        <Box
            component="img"
            src={resolvedSrc}
            alt=""
            sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
            }}
        />
    );
};

// Kleines, mit Auth geladenes Vorschaubild für die Neuigkeiten.
const NewsThumbnail: React.FC<{ url: string | null }> = ({ url }) => {
    const { src } = useAuthenticatedImage(url);
    return (
        <Box
            sx={{
                width: { xs: 72, sm: 88 },
                height: { xs: 72, sm: 88 },
                borderRadius: 'var(--pvta-radius-sm)',
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: 'var(--pvta-green-100)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {src ? (
                <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
                <ArticleOutlinedIcon sx={{ color: GREEN_700 }} />
            )}
        </Box>
    );
};

const SectionHeader: React.FC<{ title: string; actionLabel?: string; onAction?: () => void }> = ({
    title,
    actionLabel,
    onAction,
}) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, gap: 1 }}
    >
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--pvta-text)', minWidth: 0 }} noWrap>
            {title}
        </Typography>
        {actionLabel && onAction && (
            <Button
                size="small"
                onClick={onAction}
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ color: GREEN_700, fontWeight: 600, flexShrink: 0 }}
            >
                {actionLabel}
            </Button>
        )}
    </Stack>
);

const Dashboard: React.FC = () => {
    const { calendarEntries, isLoadingCalendarEntries } = useGetCalendarEntries();
    const { userArray, isLoadingUserArray, refetchUserArray } = useGetUsers();
    const { eventMedia, isLoadingEventMedia, isErrorEventMedia, eventMediaError, refetchEventMedia } = useDashboardEventMedia({
        limit: 20,
        previewLimit: 4,
    });
    const { data: storageTree } = useStorageTreeQuery();
    const { data: blogPostsPage, isLoading: isLoadingBlogPosts } = useBlogPosts(1, 5);
    const { modules: moduleSettings, isLoading: modulesLoading, isError: modulesError } = useModuleSettings();
    const storageEnabled = moduleEnabled(moduleSettings, 'storage', { isLoading: modulesLoading, isError: modulesError });
    const blogEnabled = moduleEnabled(moduleSettings, 'blog', { isLoading: modulesLoading, isError: modulesError });
    const updateCalendarEntry = useUpdateCalendarEntry();
    const deleteCalendarEntry = useDeleteCalendarEntry();
    const toggleFavoriteMutation = useToggleCalendarEntryFavorite();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [selectedEntry, setSelectedEntry] = useState<ICalendarEntry | null>(null);
    const [eventDialogOpen, setEventDialogOpen] = useState<boolean>(false);
    const { calendarEntry: detailedSelectedEntry } = useGetCalendarEntry(selectedEntry?.id, selectedEntry);

    const today = useMemo(() => new Date(), []);
    const todayStart = useMemo(() => startOfDay(today), [today]);

    const events = useMemo(
        () => calendarEntries.filter((entry: ICalendarEntry) => entry.calendar_type === 'event'),
        [calendarEntries],
    );

    const currentUser = useMemo(() => {
        if (!userArray) {
            return null;
        }
        const currentId = getCurrentUserId();
        return userArray.find((user) => user.id === currentId) ?? null;
    }, [userArray]);

    const greetingName = currentUser?.first_name?.trim() || 'Bundesbruder';

    const upcomingBirthdays = useMemo(() => {
        if (!userArray) return [];
        return userArray
            .filter((user: IAccount) => {
                if (!user.geburtstag) return false;
                if (user.verstorben && user.verstorben.trim() !== '') return false;

                const birth = new Date(user.geburtstag);
                let nextBirthday = new Date(todayStart.getFullYear(), birth.getMonth(), birth.getDate());
                if (nextBirthday.getTime() < todayStart.getTime()) {
                    nextBirthday = new Date(todayStart.getFullYear() + 1, birth.getMonth(), birth.getDate());
                }
                const diffDays = (nextBirthday.getTime() - todayStart.getTime()) / MS_PER_DAY;
                return diffDays <= 30;
            })
            .sort((a: IAccount, b: IAccount) => {
                const ab = new Date(a.geburtstag!);
                const bb = new Date(b.geburtstag!);
                let aNext = new Date(todayStart.getFullYear(), ab.getMonth(), ab.getDate());
                if (aNext.getTime() < todayStart.getTime()) aNext = new Date(todayStart.getFullYear() + 1, ab.getMonth(), ab.getDate());
                let bNext = new Date(todayStart.getFullYear(), bb.getMonth(), bb.getDate());
                if (bNext.getTime() < todayStart.getTime()) bNext = new Date(todayStart.getFullYear() + 1, bb.getMonth(), bb.getDate());
                return aNext.getTime() - bNext.getTime();
            });
    }, [userArray, todayStart]);

    const upcomingEvents = useMemo(
        () => events
            .filter((event: ICalendarEntry) => new Date(event.end_time) > today)
            .sort((a: ICalendarEntry, b: ICalendarEntry) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()),
        [events, today],
    );

    const nextEvent = upcomingEvents[0] ?? null;

    const favoriteEvents = useMemo(() => (
        events
            .filter((event) => event.is_favorite && new Date(event.end_time) > today)
            .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
            .slice(0, 4)
    ), [events, today]);

    const favoriteFolders = useMemo(() => (
        flattenFolderTree(storageTree)
            .filter((folder) => folder.is_favorite)
            .sort((a, b) => a.name.localeCompare(b.name, 'de'))
            .slice(0, 4)
    ), [storageTree]);

    const curatedEventMedia = useMemo(() => {
        const now = new Date();
        const future = eventMedia.filter((item) => new Date(item.event.end) >= now);
        const past = eventMedia.filter((item) => new Date(item.event.end) < now);
        const selectedFuture = future.slice(0, 5);
        const selectedPast = past.slice(0, 5);
        const missingFutureSlots = 5 - selectedFuture.length;
        const missingPastSlots = 5 - selectedPast.length;
        return [
            ...selectedFuture,
            ...selectedPast,
            ...past.slice(5, 5 + Math.max(0, missingFutureSlots)),
            ...future.slice(5, 5 + Math.max(0, missingPastSlots)),
        ].slice(0, 10);
    }, [eventMedia]);

    const heroImageUrl = useMemo(() => {
        if (!nextEvent) {
            return null;
        }
        const media = eventMedia.find((item) => item.event.id === nextEvent.id);
        const image = media?.preview_media.find((preview) => preview.mime_type.startsWith('image/'));
        return image?.url ?? null;
    }, [eventMedia, nextEvent]);

    const newsItems = useMemo(() => (blogPostsPage?.items ?? []).slice(0, 3), [blogPostsPage]);

    const visibleBirthdays = upcomingBirthdays.slice(0, 5);

    const openEventDetail = (entry: ICalendarEntry): void => {
        setSelectedEntry(entry);
        setEventDialogOpen(true);
    };

    const openBirthdayDetail = (user: IAccount): void => {
        navigate(`/accounts?userId=${user.id}`);
    };

    const openEventById = (eventId: number): void => {
        const event = calendarEntries.find((entry) => entry.id === eventId && entry.calendar_type === 'event');
        if (event) {
            openEventDetail(event);
            return;
        }
        navigate('/events');
    };

    const handleToggleFavorite = async (eventId: number, isFavorite: boolean): Promise<void> => {
        await toggleFavoriteMutation.mutateAsync({ eventId, isFavorite });
        setSelectedEntry((current) => (
            current && current.id === eventId ? { ...current, is_favorite: isFavorite } : current
        ));
    };

    const dialogEntry = selectedEntry && selectedEntry.id
        ? (detailedSelectedEntry ?? selectedEntry)
        : selectedEntry;

    const quickAccessItems: { label: string; icon: React.ReactNode; onClick: () => void }[] = [
        { label: 'Mitglieder', icon: <Diversity3Icon />, onClick: () => navigate('/accounts') },
        { label: 'Kalender', icon: <CalendarMonthIcon />, onClick: () => navigate('/events') },
        ...(storageEnabled
            ? [{ label: 'Dokumente', icon: <FolderOpenIcon />, onClick: () => navigate('/storage') }]
            : []),
        { label: 'Gruppen', icon: <GroupsIcon />, onClick: () => navigate('/accounts') },
    ];

    const renderHero = (): React.ReactNode => (
        <Card
            sx={{
                ...cardSx,
                ...cardHoverSx,
                ...safeCardSx,
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 'var(--pvta-radius-lg)',
                minHeight: { xs: 300, sm: 340, md: 360 },
                color: '#ffffff',
                backgroundImage: `linear-gradient(135deg, ${GREEN_700} 0%, ${GREEN_900} 100%)`,
                display: 'flex',
            }}
        >
            <HeroBackground url={heroImageUrl} />
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(7,31,18,0.05) 0%, rgba(7,31,18,0.45) 50%, rgba(7,31,18,0.92) 100%)',
                }}
            />
            <Box sx={{ position: 'relative', mt: 'auto', p: { xs: 2.5, sm: 3, md: 3.5 }, width: '100%', boxSizing: 'border-box' }}>
                <Chip
                    label={nextEvent ? 'Nächste Veranstaltung' : 'PV! Teutonia-Alemannia'}
                    size="small"
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: '#ffffff',
                        fontWeight: 700,
                        mb: 1.5,
                        backdropFilter: 'blur(4px)',
                    }}
                />
                <Typography
                    sx={{
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        fontWeight: 700,
                        fontSize: { xs: 24, sm: 30, md: 38 },
                        lineHeight: 1.15,
                        textShadow: '0 2px 12px rgba(0,0,0,0.4)',
                    }}
                >
                    {nextEvent ? nextEvent.title : 'Willkommen im Mitgliederbereich'}
                </Typography>
                {nextEvent ? (
                    <Typography sx={{ mt: 1, opacity: 0.92, fontWeight: 500, fontSize: { xs: 14, md: 16 } }}>
                        {formatLongDate(nextEvent.start_time)}
                        {nextEvent.location ? ` · ${nextEvent.location}` : ''}
                    </Typography>
                ) : (
                    <Typography sx={{ mt: 1, opacity: 0.92, fontWeight: 500, fontSize: { xs: 14, md: 16 } }}>
                        Aktuell ist keine Veranstaltung geplant.
                    </Typography>
                )}
                {nextEvent && (
                    <Button
                        onClick={() => openEventDetail(nextEvent)}
                        endIcon={<ArrowForwardRoundedIcon />}
                        sx={{
                            mt: { xs: 2, md: 2.5 },
                            backgroundColor: '#ffffff',
                            color: GREEN_900,
                            borderRadius: 'var(--pvta-radius-pill)',
                            fontWeight: 700,
                            minHeight: 44,
                            px: 2.5,
                            '&:hover': { backgroundColor: '#f1ede3' },
                        }}
                    >
                        Mehr erfahren
                    </Button>
                )}
            </Box>
        </Card>
    );

    const renderNextEvent = (): React.ReactNode => {
        const counts = nextEvent ? extractRsvpCounts(nextEvent) : null;
        return (
            <Card sx={{ ...cardSx, ...cardHoverSx, ...safeCardSx, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, minWidth: 0 }}>
                        <Box sx={makeIconCircleSx(40)}>
                            <CalendarMonthIcon fontSize="small" />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 0 }} noWrap>
                            Nächste Veranstaltung
                        </Typography>
                    </Stack>
                    {!nextEvent || !counts ? (
                        <Typography color="text.secondary">Keine anstehende Veranstaltung.</Typography>
                    ) : (
                        <Stack spacing={2} sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 20 }, lineHeight: 1.25, color: 'var(--pvta-heading)', overflowWrap: 'anywhere' }}
                            >
                                {nextEvent.title}
                            </Typography>
                            <Stack
                                spacing={1.25}
                                sx={{
                                    minWidth: 0,
                                    backgroundColor: 'var(--pvta-surface-muted)',
                                    borderRadius: 'var(--pvta-radius-sm)',
                                    p: 1.5,
                                }}
                            >
                                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                                    <EventOutlinedIcon fontSize="small" sx={{ color: GREEN_700, width: 20, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(nextEvent.start_time)}</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                                    <ScheduleOutlinedIcon fontSize="small" sx={{ color: GREEN_700, width: 20, flexShrink: 0 }} />
                                    <Typography variant="body2">
                                        {formatTime(nextEvent.start_time)} – {formatTime(nextEvent.end_time)}
                                    </Typography>
                                </Stack>
                                {nextEvent.location && (
                                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                                        <PlaceOutlinedIcon fontSize="small" sx={{ color: GREEN_700, width: 20, flexShrink: 0 }} />
                                        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{nextEvent.location}</Typography>
                                    </Stack>
                                )}
                            </Stack>
                            <RsvpStats counts={counts} />
                            {counts.going !== null && counts.going > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {counts.going} {counts.going === 1 ? 'Person nimmt teil' : 'nehmen teil'}
                                </Typography>
                            )}
                            <Button
                                fullWidth
                                onClick={() => openEventDetail(nextEvent)}
                                endIcon={<ArrowForwardRoundedIcon />}
                                sx={{
                                    ...primaryButtonSx,
                                    width: '100%',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box',
                                    minHeight: 52,
                                    borderRadius: 'var(--pvta-radius-pill)',
                                    fontSize: 16,
                                    boxShadow: '0 6px 16px rgba(18, 61, 36, 0.22)',
                                    '&:hover': { backgroundColor: GREEN_900, boxShadow: '0 8px 20px rgba(18, 61, 36, 0.28)' },
                                }}
                            >
                                Anmelden
                            </Button>
                        </Stack>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderQuickAccess = (): React.ReactNode => (
        <Card sx={{ ...cardSx, ...cardHoverSx, ...safeCardSx }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Schnellzugriff
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: { xs: 1, sm: 1.5 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                    {quickAccessItems.map((item) => (
                        <ButtonBase
                            key={item.label}
                            onClick={item.onClick}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                minWidth: 0,
                                px: 0.5,
                                py: { xs: 1.75, sm: 1.5 },
                                minHeight: 92,
                                borderRadius: 'var(--pvta-radius-md)',
                                backgroundColor: 'var(--pvta-bg)',
                                border: '1px solid var(--pvta-border)',
                                transition: 'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
                                '&:hover': { transform: 'translateY(-2px)', boxShadow: 'var(--pvta-shadow-card)' },
                                '&:hover .pvta-qa-circle': {
                                    backgroundColor: GREEN_700,
                                    color: '#ffffff',
                                },
                            }}
                        >
                            <Box
                                className="pvta-qa-circle"
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'var(--pvta-green-100)',
                                    color: GREEN_700,
                                    transition: 'background-color 160ms ease, color 160ms ease',
                                }}
                            >
                                {item.icon}
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', lineHeight: 1.15, maxWidth: '100%', overflowWrap: 'anywhere' }}>
                                {item.label}
                            </Typography>
                        </ButtonBase>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );

    const renderBirthdays = (): React.ReactNode => (
        <Card sx={{ ...cardSx, ...cardHoverSx, ...safeCardSx }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <SectionHeader title="Geburtstage" actionLabel="Alle anzeigen" onAction={() => navigate('/accounts')} />
                {visibleBirthdays.length === 0 ? (
                    <Typography color="text.secondary">Keine anstehenden Geburtstage.</Typography>
                ) : (
                    <Stack spacing={1.25}>
                        {visibleBirthdays.map((user) => {
                            const birth = new Date(user.geburtstag!);
                            let nextBirthday = new Date(todayStart.getFullYear(), birth.getMonth(), birth.getDate());
                            if (nextBirthday < todayStart) {
                                nextBirthday = new Date(todayStart.getFullYear() + 1, birth.getMonth(), birth.getDate());
                            }
                            const isBirthdayToday = nextBirthday.getTime() === todayStart.getTime();
                            return (
                                <Stack
                                    key={user.id}
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openBirthdayDetail(user)}
                                    onKeyDown={(keyboardEvent) => {
                                        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                                            keyboardEvent.preventDefault();
                                            openBirthdayDetail(user);
                                        }
                                    }}
                                    sx={{
                                        cursor: 'pointer',
                                        borderRadius: 'var(--pvta-radius-sm)',
                                        p: 0.75,
                                        '&:hover': { backgroundColor: 'var(--pvta-green-100)' },
                                    }}
                                >
                                    <UserAvatar
                                        userId={user.id}
                                        avatarUrl={user.avatar_url}
                                        hasAvatar={user.has_avatar}
                                        initials={`${user.first_name.charAt(0)}${user.last_name.charAt(0)}`}
                                        sx={{ width: 40, height: 40, fontWeight: 600, bgcolor: GREEN_700, color: '#fff', flexShrink: 0 }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                                            {user.first_name} {user.last_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {nextBirthday.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                        </Typography>
                                    </Box>
                                    {isBirthdayToday && (
                                        <StatusChip tone="gold" label="Heute" />
                                    )}
                                </Stack>
                            );
                        })}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );

    const renderPhotos = (): React.ReactNode => (
        <Card sx={{ ...cardSx, ...safeCardSx }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <SectionHeader title="Neueste Fotos von Veranstaltungen" actionLabel="Alle anzeigen" onAction={() => navigate('/events')} />
                {isErrorEventMedia && (
                    <ErrorTryRefetch refetchFunction={() => { void refetchEventMedia(); }} />
                )}
                {isErrorEventMedia && eventMediaError && (
                    <Alert severity="error" sx={{ mt: 1 }}>{eventMediaError.message}</Alert>
                )}
                {isLoadingEventMedia && (
                    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>
                        {[0, 1, 2].map((item) => (
                            <Skeleton key={item} variant="rounded" width={220} height={200} sx={{ flex: '0 0 auto', borderRadius: 'var(--pvta-radius-sm)' }} />
                        ))}
                    </Box>
                )}
                {!isLoadingEventMedia && !isErrorEventMedia && curatedEventMedia.length === 0 && (
                    <Typography color="text.secondary">Keine aktuellen Veranstaltungsfotos.</Typography>
                )}
                {!isLoadingEventMedia && !isErrorEventMedia && curatedEventMedia.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            overflowX: 'auto',
                            pb: 1,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        {curatedEventMedia.map((item) => (
                            <Box
                                key={item.event.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => openEventById(item.event.id)}
                                onKeyDown={(keyboardEvent) => {
                                    if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                                        keyboardEvent.preventDefault();
                                        openEventById(item.event.id);
                                    }
                                }}
                                sx={{
                                    flex: '0 0 auto',
                                    width: { xs: 200, md: 224 },
                                    cursor: 'pointer',
                                    transition: 'transform 160ms ease',
                                    '&:hover': { transform: 'translateY(-2px)' },
                                }}
                            >
                                <Box sx={{ borderRadius: 'var(--pvta-radius-sm)', overflow: 'hidden' }}>
                                    <EventMediaMosaic previewMedia={item.preview_media} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }} noWrap>
                                    {item.event.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {formatLongDate(item.event.start)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {`${item.media_count} Fotos`}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );

    const renderNews = (): React.ReactNode => (
        <Card sx={{ ...cardSx, ...safeCardSx }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <SectionHeader title="Neuigkeiten" actionLabel="Alle Nachrichten" onAction={() => navigate('/blog')} />
                {isLoadingBlogPosts && (
                    <Stack spacing={1.5}>
                        {[0, 1].map((item) => (
                            <Skeleton key={item} variant="rounded" height={96} sx={{ borderRadius: 'var(--pvta-radius-sm)' }} />
                        ))}
                    </Stack>
                )}
                {!isLoadingBlogPosts && newsItems.length === 0 && (
                    <Typography color="text.secondary">Aktuell keine Neuigkeiten.</Typography>
                )}
                {!isLoadingBlogPosts && newsItems.length > 0 && (
                    <Stack spacing={1.5}>
                        {newsItems.map((post, index) => {
                            const thumbnailUrl = post.coverImageUrl ?? post.previewImages[0]?.url ?? null;
                            const description = post.teaser || post.fallbackText;
                            const highlighted = index === 0;
                            return (
                                <Stack
                                    key={post.id}
                                    direction="row"
                                    spacing={2}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate(`/blog/${post.id}`)}
                                    onKeyDown={(keyboardEvent) => {
                                        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                                            keyboardEvent.preventDefault();
                                            navigate(`/blog/${post.id}`);
                                        }
                                    }}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 'var(--pvta-radius-sm)',
                                        cursor: 'pointer',
                                        alignItems: 'flex-start',
                                        backgroundColor: highlighted ? 'var(--pvta-green-100)' : 'transparent',
                                        borderLeft: highlighted ? `3px solid ${GREEN_700}` : '3px solid transparent',
                                        transition: 'background-color 160ms ease',
                                        '&:hover': { backgroundColor: 'var(--pvta-green-100)' },
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                                            {post.title}
                                        </Typography>
                                        {description && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.5,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {description}
                                            </Typography>
                                        )}
                                        {post.createdAt && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {formatLongDate(post.createdAt)}
                                            </Typography>
                                        )}
                                    </Box>
                                    <NewsThumbnail url={thumbnailUrl} />
                                </Stack>
                            );
                        })}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );

    const renderFavorites = (): React.ReactNode => {
        const showFolders = favoriteFolders.length > 0 && storageEnabled;
        if (favoriteEvents.length === 0 && !showFolders) {
            return null;
        }
        return (
            <Card sx={{ ...cardSx, ...cardHoverSx, ...safeCardSx }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        Favoriten
                    </Typography>
                    <Stack spacing={1.5}>
                        {favoriteEvents.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                                    Veranstaltungen
                                </Typography>
                                <Stack spacing={1}>
                                    {favoriteEvents.map((event) => (
                                        <Stack
                                            key={`favorite-event-${event.id}`}
                                            direction="row"
                                            spacing={1}
                                            alignItems="flex-start"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openEventDetail(event)}
                                            onKeyDown={(keyboardEvent) => {
                                                if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                                                    keyboardEvent.preventDefault();
                                                    openEventDetail(event);
                                                }
                                            }}
                                            sx={{ p: 1, borderRadius: 'var(--pvta-radius-sm)', cursor: 'pointer', '&:hover': { backgroundColor: 'var(--pvta-green-100)' } }}
                                        >
                                            <StarRoundedIcon sx={{ color: 'var(--pvta-gold)', fontSize: 18, mt: 0.15 }} />
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{event.title}</Typography>
                                                <Typography variant="caption" color="text.secondary">{formatDate(event.start_time)}</Typography>
                                            </Box>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                        {favoriteFolders.length > 0 && storageEnabled && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                                    Datenablage
                                </Typography>
                                <Stack spacing={1}>
                                    {favoriteFolders.map((folder) => (
                                        <Stack
                                            key={`favorite-folder-${folder.id}`}
                                            direction="row"
                                            spacing={1}
                                            alignItems="flex-start"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => navigate('/storage')}
                                            onKeyDown={(keyboardEvent) => {
                                                if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                                                    keyboardEvent.preventDefault();
                                                    navigate('/storage');
                                                }
                                            }}
                                            sx={{ p: 1, borderRadius: 'var(--pvta-radius-sm)', cursor: 'pointer', '&:hover': { backgroundColor: 'var(--pvta-green-100)' } }}
                                        >
                                            <FolderOpenIcon sx={{ color: GREEN_700, fontSize: 18, mt: 0.15 }} />
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{folder.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {typeof folder.file_count === 'number' ? `${folder.file_count} Dateien` : 'Ordner'}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    if (isLoadingUserArray || isLoadingCalendarEntries) {
        return (
            <Box sx={{ py: { xs: 3, md: 5 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
                    <Skeleton variant="text" width={320} height={56} sx={{ mb: 1, maxWidth: '100%' }} />
                    <Skeleton variant="text" width={240} height={24} sx={{ mb: 4 }} />
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 7fr) minmax(0, 5fr)' },
                            gap: 3,
                        }}
                    >
                        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 'var(--pvta-radius-lg)' }} />
                        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 'var(--pvta-radius-md)' }} />
                    </Box>
                </Container>
            </Box>
        );
    }

    if (!userArray) {
        return <ErrorTryRefetch refetchFunction={refetchUserArray} />;
    }

    return (
        <Box sx={{ py: { xs: 3, md: 5 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
                <Box sx={{ mb: { xs: 2.5, md: 4 }, maxWidth: 680 }}>
                    <Typography
                        sx={{
                            fontFamily: 'Georgia, "Times New Roman", serif',
                            fontWeight: 700,
                            fontSize: { xs: 24, sm: 30, md: 40 },
                            lineHeight: 1.15,
                            color: GREEN_900,
                        }}
                    >
                        {`Willkommen zurück, ${greetingName}! 👋`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: 13, md: 14 } }}>
                        Hier ist dein Überblick über alles Wichtige.
                    </Typography>
                </Box>

                {isDesktop ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)',
                            gap: 3,
                            alignItems: 'start',
                        }}
                    >
                        <Stack spacing={3} sx={{ minWidth: 0 }}>
                            {renderHero()}
                            {renderPhotos()}
                            {blogEnabled && renderNews()}
                        </Stack>
                        <Stack spacing={3} sx={{ minWidth: 0 }}>
                            {renderNextEvent()}
                            {renderQuickAccess()}
                            {renderBirthdays()}
                            {renderFavorites()}
                        </Stack>
                    </Box>
                ) : (
                    <Stack spacing={2.5} sx={{ width: '100%', minWidth: 0 }}>
                        {renderHero()}
                        {renderNextEvent()}
                        {renderQuickAccess()}
                        {renderBirthdays()}
                        {renderPhotos()}
                        {blogEnabled && renderNews()}
                        {renderFavorites()}
                    </Stack>
                )}

                {eventDialogOpen && dialogEntry && (
                    <CalendarEventEdit
                        event={dialogEntry}
                        onClose={() => setEventDialogOpen(false)}
                        onSave={(entry) => updateCalendarEntry.mutate(entry, { onSuccess: () => setEventDialogOpen(false) })}
                        onDelete={(id) => deleteCalendarEntry.mutate(id, { onSuccess: () => setEventDialogOpen(false) })}
                        onToggleFavorite={handleToggleFavorite}
                        isFavoritePending={toggleFavoriteMutation.isPending}
                    />
                )}
            </Container>
        </Box>
    );
};

export default Dashboard;
